import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTaxonomy } from "./useTaxonomy";

/**
 * Hook to convert taxonomy ID to code when loading data and vice versa
 * This ensures the form displays codes while the backend works with IDs
 */
export const useTaxonomyFieldConverter = (
  fieldPath: string,
  taxonomyName: keyof ReturnType<typeof useTaxonomy>
) => {
  const { watch, setValue } = useFormContext();
  const taxonomy = useTaxonomy();
  const fieldValue = watch(fieldPath);

  useEffect(() => {
    if (!taxonomy || !taxonomy[taxonomyName] || !fieldValue) return;

    const taxonomyGroup = taxonomy[taxonomyName];

    // Check if the current value is an ID (number or numeric string)
    const isNumericId = !isNaN(Number(fieldValue)) && fieldValue !== "";

    if (isNumericId) {
      // Convert ID to code
      const id = Number(fieldValue);

      // Handle different taxonomy structures
      let taxonomyItem;
      if (taxonomyGroup.byId && taxonomyGroup.byId[id]) {
        // Standard structure with byId lookup
        taxonomyItem = taxonomyGroup.byId[id];
      } else if (Array.isArray(taxonomyGroup)) {
        // Direct array structure - search by id
        taxonomyItem = taxonomyGroup.find((item: any) => item.id === id);
      }

      if (taxonomyItem && taxonomyItem.code !== fieldValue) {
        console.log(
          `Converting ${taxonomyName} ID ${id} to code ${taxonomyItem.code}`
        );
        setValue(fieldPath, taxonomyItem.code);
      }
    }
  }, [fieldValue, taxonomy, taxonomyName, fieldPath, setValue]);
};

/**
 * Helper function to convert taxonomy code to ID
 */
export const getTaxonomyIdFromCode = (
  taxonomy: ReturnType<typeof useTaxonomy>,
  taxonomyName: keyof ReturnType<typeof useTaxonomy>,
  code: string | undefined
): number | string => {
  if (!code || !taxonomy || !taxonomy[taxonomyName]) {
    return code || "";
  }

  const taxonomyGroup = taxonomy[taxonomyName];

  // If it's already an ID (numeric), return as-is
  if (!isNaN(Number(code)) && code !== "") {
    return Number(code);
  }

  // Convert code to ID
  const taxonomyItem = taxonomyGroup.byCode[code];
  if (taxonomyItem) {
    return taxonomyItem.id;
  }

  // If not found, return the original value
  return code;
};

/**
 * Helper function to convert taxonomy ID to code
 */
export const getTaxonomyCodeFromId = (
  taxonomy: ReturnType<typeof useTaxonomy>,
  taxonomyName: keyof ReturnType<typeof useTaxonomy>,
  id: number | string | undefined
): string => {
  if (!id || !taxonomy || !taxonomy[taxonomyName]) {
    return String(id || "");
  }

  const taxonomyGroup = taxonomy[taxonomyName];

  // If it's already a code (contains a dot), return as-is
  if (String(id).includes(".")) {
    return String(id);
  }

  // Convert ID to code
  const numericId = Number(id);
  const taxonomyItem = taxonomyGroup.byId[numericId];
  if (taxonomyItem) {
    return taxonomyItem.code;
  }

  // If not found, return the original value as string
  return String(id);
};
